import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

const Account = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const fetchOrders = async (customerEmail) => {
    setLoading(true);
    setHasSearched(true);
    try {
      const data = await api.getOrdersByEmail(customerEmail);
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email.trim()) {
      fetchOrders(email);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Processing':
        return 'bg-blue-100 text-blue-800';
      case 'Shipped':
        return 'bg-purple-100 text-purple-800';
      case 'Delivered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-medium text-gray-900 mb-8">My Account</h1>

        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="orders">Orders</TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-8">
            <Card className="p-6 mb-6">
              <h2 className="text-xl font-medium text-gray-900 mb-4">Find Your Orders</h2>
              <form onSubmit={handleSubmit} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="email">Enter your email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-2"
                  />
                </div>
                <div className="flex items-end">
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Searching...' : 'Find Orders'}
                  </Button>
                </div>
              </form>
            </Card>

            {loading ? (
              <Card className="p-12 text-center">
                <p className="text-xl text-gray-600">Loading orders...</p>
              </Card>
            ) : hasSearched && orders.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-xl text-gray-600 mb-4">No orders found</p>
                <p className="text-gray-500">No orders found for this email address</p>
              </Card>
            ) : orders.length > 0 ? (
              <div className="space-y-6">
                {orders.map((order) => (
                  <Card key={order._id} className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">
                          Order #{order.order_number}
                        </h3>
                        <p className="text-sm text-gray-600">
                          Placed on {new Date(order.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      <Badge className={getStatusColor(order.status)}>
                        {order.status}
                      </Badge>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between py-2">
                          <div className="flex items-center space-x-4">
                            <img
                              src={item.product_image}
                              alt={item.product_name}
                              className="w-16 h-16 object-cover rounded"
                            />
                            <div>
                              <p className="font-medium text-gray-900">{item.product_name}</p>
                              <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                              {item.customization?.message && (
                                <p className="text-sm text-gray-600 italic mt-1">
                                  Message: "{item.customization.message}"
                                </p>
                              )}
                            </div>
                          </div>
                          <p className="font-medium text-gray-900">
                            ${(item.price * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-gray-200 mt-4 pt-4">
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-600">
                          <p className="font-medium text-gray-900 mb-1">Shipping Address:</p>
                          <p>{order.shipping_address.first_name} {order.shipping_address.last_name}</p>
                          <p>{order.shipping_address.address}</p>
                          <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600 mb-1">Total</p>
                          <p className="text-2xl font-medium text-gray-900">${order.total.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : null}
          </TabsContent>

          <TabsContent value="profile" className="mt-8">
            <Card className="p-6">
              <h2 className="text-xl font-medium text-gray-900 mb-6">Profile Information</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="text-gray-900">{email || 'Not logged in'}</p>
                </div>
                <div className="pt-4">
                  <p className="text-sm text-gray-500 italic">
                    Full user authentication will be available in a future update. Currently, you can track orders by entering your email address.
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Account;
